import React, { useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Page } from '@accedo/vdkweb-tv-ui';
import classNames from 'classnames';
import moment from 'moment-mini';
import {
  ageGroupsKeys,
  streamsAgeGroup,
  playerConstants,
} from '#/config/constants';
import {
  sendVideoLiveHistory,
  loadEpg,
} from '#/redux/modules/appPlayer/actions';

import { getCurrentProfileAgeGroup } from '#/redux/modules/onboarding/selectors';
import { getEpgProgramList } from '#/redux/modules/appPlayer/selectors';
import styles from './LiveTV.scss';

import LivePlayer from '#/components/LivePlayer/LivePlayer';
import PlayerCCButton from '#/widgets/ui/Buttons/PlayerCCButton';
import PlayerBackButton from '#/widgets/ui/Buttons/PlayerBackButton';
import liveLogoUri from '#/static/images/icons/live-logo.svg';
import liveLogoTVri from '#/static/images/icons/live-logo-tv.svg';
import { LIVE_TV } from '#/utils/navigationHelper';
import { isWorkstation } from '#/utils/componentStyleConfig';

import { FocusDiv } from '#/widgets/ui';
import { useIsInitialDeeplink } from '#/hooks/useIsInitialDeeplink';

const { PAGE, CONTAINER, PLAYER_CC_BUTTON } = LIVE_TV;

const navMap = {
  PAGE: {
    id: PAGE,
    forwardFocus: CONTAINER,
  },
  CONTAINER: {
    id: CONTAINER,
    nextup: PLAYER_CC_BUTTON,
  },
  PLAYER_CC_BUTTON: {
    id: PLAYER_CC_BUTTON,
    nextdown: CONTAINER,
  },
};
const VOLUME_CONTROL_CLASS = 'vjs-volume-panel';
const isPopulatedString = value =>
  typeof value === 'string' && value.trim().length > 0;

const LiveTV = ({ content, match, location }) => {
  const [currentProgram, setCurrentProgram] = useState();
  const [nextProgram, setNextProgram] = useState();
  const dispatch = useDispatch();
  const currentPath = 'livetv';
  const topOverlay = useRef();
  const topOverlayHideTimer = useRef();
  const epgUpdateTimer = useRef();
  const currentProgramIndex = useRef(0);
  const epgProgramData = useSelector(getEpgProgramList);

  // Deep linking ------------------------------------------------------------------
  // Only deeplinked route will have age param.
  // If that's the case, then force the stream
  useIsInitialDeeplink(location);
  const streamAge = Number(match?.params?.age || 0);
  // Deep linking END --------------------------------------------------------------

  const selectedAgeGroup = useSelector(state =>
    getCurrentProfileAgeGroup(state),
  );
  const selectedStream = useMemo(() => {
    if (streamAge > 0) {
      return (
        streamsAgeGroup?.[`STREAM_${streamAge}Up`] || streamsAgeGroup.STREAM_8Up
      );
    }
    switch (selectedAgeGroup) {
      case ageGroupsKeys.TWO_UP:
        return streamsAgeGroup.STREAM_2Up;
      case ageGroupsKeys.FIVE_UP:
        return streamsAgeGroup.STREAM_5Up;
      default:
        return streamsAgeGroup.STREAM_8Up;
    }
  }, [selectedAgeGroup, streamAge]);

  const [liveStreamUrl, epgUrl, resolvedStream] = useMemo(() => {
    const { key, epgKey } = selectedStream || {};
    const pages = [content?.[currentPath], ...Object.values(content || {})]
      .filter(Boolean)
      .filter((page, index, arr) => arr.indexOf(page) === index);

    const liveContent =
      pages.find(page =>
        Object.keys(page || {}).some(pageKey => /^stream/i.test(pageKey || '')),
      ) || {};

    const liveContentEntries = Object.entries(liveContent);
    const keyLookup = liveContentEntries.reduce((lookup, [entryKey, value]) => {
      lookup[entryKey.toLowerCase()] = value;
      return lookup;
    }, {});

    const selectedAgeHint = `${selectedStream?.title || ''}`.match(/\d+/)?.[0];
    const streamAgeKey = selectedAgeHint
      ? liveContentEntries.find(([entryKey, value]) => {
          const entryKeyLower = entryKey.toLowerCase();
          return (
            /^stream/.test(entryKeyLower) &&
            entryKeyLower.includes(selectedAgeHint) &&
            isPopulatedString(value)
          );
        })?.[0]
      : undefined;
    const epgAgeKey = selectedAgeHint
      ? liveContentEntries.find(([entryKey, value]) => {
          const entryKeyLower = entryKey.toLowerCase();
          return (
            /^epg/.test(entryKeyLower) &&
            entryKeyLower.includes(selectedAgeHint) &&
            isPopulatedString(value)
          );
        })?.[0]
      : undefined;

    const selectedLiveStreamUrl =
      liveContent?.[key] ||
      keyLookup?.[`${key || ''}`.toLowerCase()] ||
      liveContent?.[streamAgeKey];
    const selectedEpgUrl =
      liveContent?.[epgKey] ||
      keyLookup?.[`${epgKey || ''}`.toLowerCase()] ||
      liveContent?.[epgAgeKey];

    if (isPopulatedString(selectedLiveStreamUrl)) {
      return [selectedLiveStreamUrl, selectedEpgUrl, selectedStream];
    }

    const fallbackStream = Object.values(streamsAgeGroup).find(stream => {
      const streamUrl =
        liveContent?.[stream?.key] ||
        keyLookup?.[`${stream?.key || ''}`.toLowerCase()];
      return isPopulatedString(streamUrl);
    });

    if (fallbackStream) {
      return [
        liveContent?.[fallbackStream.key] ||
          keyLookup?.[fallbackStream.key.toLowerCase()],
        liveContent?.[fallbackStream.epgKey] ||
          keyLookup?.[fallbackStream.epgKey.toLowerCase()],
        fallbackStream,
      ];
    }

    const firstStreamEntry = liveContentEntries.find(
      ([entryKey, value]) =>
        /^stream/i.test(entryKey || '') && isPopulatedString(value),
    );

    if (!firstStreamEntry) {
      return [undefined, undefined, selectedStream];
    }

    const [firstStreamKey, firstStreamUrl] = firstStreamEntry;
    const matchedEpgEntry = liveContentEntries.find(
      ([entryKey, value]) =>
        /^epg/i.test(entryKey || '') &&
        entryKey
          .toLowerCase()
          .includes(firstStreamKey.toLowerCase().replace('stream', '')) &&
        isPopulatedString(value),
    );

    return [firstStreamUrl, matchedEpgEntry?.[1], selectedStream];
  }, [content, selectedStream]);

  useEffect(() => {
    if (epgUrl && !epgProgramData[epgUrl]) {
      dispatch(loadEpg(epgUrl));
    }
  }, [epgUrl]);

  useEffect(() => {
    if (!epgProgramData[epgUrl] || epgProgramData[epgUrl].length <= 0) {
      return;
    }

    const epgUpdate = () => {
      clearTimeout(epgUpdateTimer);

      const programList = epgProgramData[epgUrl];
      const currentTime = Date.now() / 1000;

      // check if same program is running for early return
      if (
        currentTime >= programList[currentProgramIndex.current]?.start &&
        currentTime <= programList[currentProgramIndex.current]?.end
      ) {
        epgUpdateTimer.current = setTimeout(
          epgUpdate,
          (60 - new Date().getSeconds()) * 1000,
        );
        return;
      }

      const programIndex = programList.findIndex(
        program => currentTime >= program.start && currentTime <= program.stop,
      );

      if (programIndex > -1) {
        currentProgramIndex.current = programIndex;
        setCurrentProgram(programList[programIndex]);

        setNextProgram(programList[programIndex + 1]);
        epgUpdateTimer.current = setTimeout(
          epgUpdate,
          (60 - new Date().getSeconds()) * 1000,
        );
      } else {
        currentProgramIndex.current = 0;
        dispatch(loadEpg(epgUrl));
        epgUpdateTimer.current = null;
      }
    };
    epgUpdate();

    return () => clearTimeout(epgUpdateTimer.current);
  }, [epgProgramData]);

  // send live history event when unmounted
  useEffect(() => {
    return () =>
      dispatch(
        sendVideoLiveHistory(`Sensical${resolvedStream?.title}`, liveStreamUrl),
      );
  }, []);

  // Effect to transition overlay opacity for animation
  useEffect(() => {
    if (topOverlay.current) {
      const volumeControl = topOverlay.current.previousSibling.getElementsByClassName(
        VOLUME_CONTROL_CLASS,
      )[0];
      requestAnimationFrame(() => {
        topOverlay.current.style.opacity = 1;
        topOverlay.current.style.visibility = 'visible';
        if (volumeControl) {
          volumeControl.style.opacity = 1;
          volumeControl.style.visibility = 'visible';
        }
      });

      const onMouseInteraction = () => {
        topOverlay.current.style.opacity = 1;
        if (volumeControl) {
          volumeControl.style.visibility = 'visible';
          volumeControl.style.opacity = 1;
        }
        document.body.style.cursor = '';
        clearTimeout(topOverlayHideTimer.current);
        topOverlayHideTimer.current = setTimeout(() => {
          topOverlay.current.style.opacity = 0;
          if (volumeControl) {
            volumeControl.style.opacity = 0;
            volumeControl.style.visibility = 'hidden';
          }
          document.body.style.cursor = 'none';
        }, playerConstants.HIDE_OVERLAY_TIMEOUT);
      };

      const onKeyDown = () => {
        if (isWorkstation()) {
          return;
        }
        topOverlay.current.style.opacity = 1;
        clearTimeout(topOverlayHideTimer.current);
        topOverlayHideTimer.current = setTimeout(() => {
          topOverlay.current.style.opacity = 0;
        }, playerConstants.HIDE_OVERLAY_TIMEOUT);
      };

      document.addEventListener('mousemove', onMouseInteraction);
      document.addEventListener('click', onMouseInteraction);
      document.addEventListener('keydown', onKeyDown);
      return () => {
        document.body.style.cursor = '';
        clearTimeout(topOverlayHideTimer.current);
        document.removeEventListener('mousemove', onMouseInteraction);
        document.removeEventListener('click', onMouseInteraction);
        document.removeEventListener('keydown', onKeyDown);
      };
    }
    document.body.style.cursor = '';
    clearTimeout(topOverlayHideTimer.current);
  }, [topOverlay.current]);

  const { title, timeRange, desc } = currentProgram || {};
  const { logoUri } = resolvedStream || selectedStream || {};
  const nextProgramDetails = {
    title: nextProgram?.title,
    upNextAt: `Up next at ${moment.unix(nextProgram?.start).format('h:mm A')}`,
  };

  return (
    <Page nav={navMap.PAGE}>
      <FocusDiv nav={navMap.CONTAINER}>
        <div
          className={classNames(styles.liveTvContainer, {
            [styles.isTv]: !isWorkstation(),
          })}
        >
          <LivePlayer key={liveStreamUrl} url={liveStreamUrl} />
          <div className={styles.topOverlay} ref={topOverlay}>
            {isWorkstation() && <PlayerBackButton />}
            <div className={styles.detailsContent}>
              <p>{timeRange}</p>
              <h2 dangerouslySetInnerHTML={{ __html: title }} />
              <p
                dangerouslySetInnerHTML={{
                  __html: currentProgram?.['sub-title'] || desc,
                }}
              />
            </div>
            <div className={styles.toggles}>
              <img
                className={styles.liveLogo}
                src={!isWorkstation() ? liveLogoTVri : liveLogoUri}
                alt="live"
              />
              <div>
                <PlayerCCButton
                  containerClassName={styles.ccButtonContainer}
                  className={styles.ccButton}
                  nav={navMap.PLAYER_CC_BUTTON}
                />
              </div>
            </div>
            <div className={styles.gradient}>
              {!isWorkstation() && nextProgram && (
                <div className={styles.nextProgram}>
                  <p>{nextProgramDetails.upNextAt}</p>
                  <h2
                    dangerouslySetInnerHTML={{
                      __html: nextProgramDetails.title,
                    }}
                  />
                </div>
              )}
              <img className={styles.logoImg} src={logoUri} alt={title} />
            </div>
          </div>
        </div>
      </FocusDiv>
    </Page>
  );
};

LiveTV.propTypes = {
  content: PropTypes.object,
  // history: PropTypes.object,
  location: PropTypes.object,
  match: PropTypes.object,
};

export default LiveTV;
